/* ============================================================
   StarOLED 2-in-1 — Landing Page Copy Data
   Single source of truth for all marketing copy.
   Consumed by js/main.js to render page sections.

   CLAIMS POLICY — read before editing any string in this file.

   This product is a UV light trap. UV traps catch a wide range of
   flying insects, but they are weak specifically against mosquitoes:
   mosquitoes locate people mainly by exhaled CO2 and body heat, not
   by UV light. Published field work (see the American Mosquito
   Control Association's position) found mosquitoes made up only a
   few percent of what these devices catch, with no measurable
   difference in mosquito numbers between yards with and without one.

   Therefore, in this file:
     • Describe the product as a UV insect trap, not a "mosquito
       zapper" or "mosquito killer".
     • Never claim it protects a person or an area from bites, or
       that it replaces repellent or professional yard treatment.
     • Never use "eliminates", "protected", or "bite-free".
     • Never state or imply how many customers bought, chose, or
       preferred anything until real sales data supports it.
   These are the claim types Meta, TikTok and the FTC enforce
   against most aggressively, and the About page already states the
   honest version — the two must not contradict each other.
   ============================================================ */

const COPY = {
  product: {
    brand: "StarOLED",
    name: "StarOLED 2-in-1 Solar UV Insect Trap & Landscape Light",
    shortName: "StarOLED 2-in-1",
    tagline: "Solar-Powered. Chemical-Free. Always On Duty."
  },

  hero: {
    superHeadline: "SOLAR-POWERED. CHEMICAL-FREE.",
    headline: "Solar UV Insect Trap & Garden Light — Automatic, Every Night.",
    subheadline:
      "No cords. No chemicals. No sprays. A 360° UV insect trap and a landscape light in one, powered entirely by the sun.",
    bullets: [
      {
        icon: "sun",
        title: "Dusk-to-Dawn Automation",
        text: "Charges all day, runs all night. It turns itself on at sunset and off at sunrise—you never touch a switch."
      },
      {
        icon: "toggle",
        title: "Dual-Mode Utility",
        text: "Switch instantly between elegant white accent lighting and the UV insect trap. One stake, two jobs."
      },
      {
        icon: "shield",
        /* Previously "100% Weatherproof (IP65)". The supplier's own product
           artwork states IPX4 — splash resistance only — so IP65 was both
           unverified and materially higher than the only evidence we have,
           and "leave it out all season" is not supportable at IPX4. Make no
           specific ingress claim until the rating is confirmed with the
           manufacturer. The About page carries the same wording — if you
           restore a rating here, update it there in the same change. */
        title: "Weather Resistant",
        text: "Built to handle rain, wind, and summer heat out in the garden."
      }
    ],
    cta: {
      primary: "Shop StarOLED",
      secondary: "See Bundle Deals",
      /* Must match the Shipping Policy exactly. Stock is held by our
         overseas supplier, so do not promise same-day or 24-hour
         dispatch here. */
      urgency: "Free worldwide shipping on every order — dispatched in 1–3 business days."
    }
  },

  painPleasure: {
    heading: "Why We Built It This Way.",
    subheading:
      "Most outdoor insect gadgets need an outlet, a refill, or both. We wanted one that needed neither.",
    pains: [
      {
        icon: "spray",
        title: "Sprays And Lotions",
        text: "They work, but they go on your skin, need reapplying every few hours, and you have to remember to buy more."
      },
      {
        icon: "money",
        title: "Recurring Yard Treatments",
        text: "$60–$150 per professional treatment, repeated through the season. Effective, but it is a bill that never stops."
      },
      {
        icon: "plug",
        title: "Extension Cord Chaos",
        text: "Traditional plug-in zappers chain you to the nearest outlet. Cords across the lawn, tripping hazards, and mains power draw."
      }
    ],
    pleasures: [
      {
        icon: "leaf",
        title: "Zero Chemicals, Zero Smell",
        text: "UV light draws flying insects toward a caged electric grid. Nothing is sprayed onto your skin, your food, or into the air."
      },
      {
        icon: "wallet",
        title: "Pay Once, Runs All Season",
        text: "No refills, no cartridges, no subscriptions. The sun covers the running cost."
      },
      {
        icon: "stake",
        title: "Stake It Anywhere in 10 Seconds",
        text: "Completely wireless. Push the stake into soil anywhere flying insects gather—patio edge, garden bed, poolside—done."
      }
    ],
    /* Rendered under the two columns by sections/pain-pleasure.liquid.
       Keeps the comparison honest: this is an addition to the options
       above, not a replacement for them. Do not delete it — without it
       the two columns read as "this replaces repellent", which is the
       single most enforceable claim on the page. */
    footnote:
      "A UV trap is a complement to these methods, not a replacement. Mosquitoes in particular home in on exhaled CO2 and body heat rather than UV light, so if you are trying to avoid bites, keep using a repellent."
  },

  bundles: {
    heading: "Choose Your Coverage",
    subheading: "Flying insects move around the whole yard. The 2-Pack and 4-Pack let you cover more than one area at once.",
    currency: "$",
    options: [
      {
        id: "pack1",
        qty: 1,
        name: "Starter Pack",
        coverage: "1 Area",
        price: 29.99,
        compareAt: null,  /* derived at render time - see main.js */
        shipping: 0,
        shippingLabel: "FREE Shipping",
        badge: null,
        savingsLabel: null,
        perUnit: 29.99
      },
      {
        id: "pack2",
        qty: 2,
        name: "Front & Back Yard",
        coverage: "2 Areas",
        price: 49.99,
        compareAt: null,  /* derived at render time - see main.js */
        shipping: 0,
        shippingLabel: "FREE Shipping",
        badge: "Most Popular",
        /* savingsLabel intentionally removed: the saving is calculated at
           render time from the real single-unit price. See main.js. */
        savingsLabel: null,
        perUnit: 24.99
      },
      {
        id: "pack4",
        qty: 4,
        name: "Full Perimeter",
        coverage: "4 Areas",
        price: 89.99,
        compareAt: null,  /* derived at render time - see main.js */
        shipping: 0,
        shippingLabel: "FREE Shipping",
        badge: "Best Value",
        savingsLabel: null,
        perUnit: 22.49
      }
    ],
    guaranteeNote: "Every bundle is covered by our 30-day money-back guarantee."
  },

  /* ------------------------------------------------------------------
     CUSTOMER REVIEWS — REAL REVIEWS ONLY.

     This array is intentionally EMPTY. Reviews are served exclusively by
     the Judge.me app (see sections/testimonials.liquid), which collects
     them from verified purchasers only.

     DO NOT hand-write entries here. Publishing invented reviews — or
     labelling any review "Verified" without an actual verification
     process behind it — violates:
       • FTC Rule on the Use of Consumer Reviews and Testimonials
         (16 CFR Part 465), which carries civil penalties
       • Meta Advertising Standards — Unacceptable Business Practices
       • TikTok Advertising Policies — Deceptive Practices

     When Judge.me has real reviews, they render automatically. Until
     then main.js shows an honest "no reviews yet" state.
     ------------------------------------------------------------------ */
  testimonials: {
    heading: "Customer Reviews",
    subheading: "Every review below comes from a verified purchase. We do not write, edit, or filter them.",
    emptyState: {
      title: "No reviews yet",
      text: "StarOLED is newly launched, so there are no customer reviews to show yet. If you buy one, you'll get an email invitation to leave an honest review — good or bad — and it will appear here unedited."
    },
    reviews: []
  },

  faqs: {
    heading: "Everything You're Wondering",
    items: [
      {
        q: "Will this stop me getting bitten by mosquitoes?",
        a: "We want to be straight with you: on its own, no. UV traps catch a broad range of flying insects, but mosquitoes are the hardest group for them to catch, because mosquitoes track exhaled CO2 and body heat rather than UV light. Independent field research has found mosquitoes make up only a small share of what UV traps catch. Buy StarOLED as a garden light that also reduces flying-insect traffic around it — not as a replacement for repellent, screens, or removing standing water."
      },
      {
        q: "Does it work on cloudy days?",
        a: "Yes. The solar panel collects ambient light even in overcast conditions, and the battery stores surplus charge from sunnier days. A full charge powers the lamp through the entire night—typically 8–10 hours of continuous running."
      },
      {
        q: "Is it safe around children and pets?",
        a: "StarOLED is designed with a protective outer cage that keeps fingers and paws away from the internal electric grid, and it uses no chemicals, fumes, or sprays — so there is nothing to inhale or track indoors. As with any electrical appliance, place it out of reach of small children, supervise young children around it, and do not insert objects through the cage."
      },
      {
        q: "What is your return policy?",
        a: "Every order is covered by our 30-day money-back guarantee. Use it in your own yard for a full month; if you're not happy with it for any reason, contact support@staroled.online for a return label and a full refund of the purchase price. No restocking fees. Full details are in our Refund Policy."
      },
      {
        q: "How much area does one lamp cover?",
        a: "The UV light draws flying insects from roughly 500 sq ft — about the size of a standard patio and seating area. That figure describes the lamp's attraction radius, not a guaranteed bite-free zone. For a wider yard, spacing units 20–25 feet apart along the perimeter gives more even coverage, which is what the 4-Pack is intended for."
      },
      {
        q: "How long does delivery take?",
        a: "Orders are dispatched within 1–3 business days and typically arrive 8–14 business days after that. Our stock ships from our overseas manufacturing partner, which is why delivery takes longer than a domestic warehouse would. Shipping is free worldwide, and you'll get a tracking number by email as soon as your order leaves. Depending on your country, import duties or taxes may be charged on delivery — those are set by your local authorities and aren't included in our price."
      },
      {
        q: "Do I need any wiring or setup?",
        a: "None. There are no cords, plugs, or batteries to replace. Push the ground stake into soil in a spot that gets daytime sun, and it starts working that same night."
      }
    ]
  },

  trust: {
    badges: [
      { icon: "truck", label: "FREE Shipping on All Orders" },
      { icon: "shield-check", label: "30-Day Money-Back Guarantee" },
      { icon: "lock", label: "SSL Secured Checkout" },
      { icon: "medal", label: "12-Month Warranty" }
    ],
    /* Compact badge row shown directly beneath the checkout buttons. */
    checkoutBadges: [
      { icon: "lock", label: "SSL Secured Checkout" },
      { icon: "shield-check", label: "30-Day Money-Back" },
      { icon: "card", label: "Safe Payment" }
    ],
    guarantee: {
      heading: "30-Day Money-Back Guarantee",
      text: "Try StarOLED at home for 30 nights. If you're not happy with it for any reason, send it back for a full refund of the purchase price — we'll cover the return shipping label. No restocking fee."
    }
  },

  /* Inventory scarcity badge.
     IMPORTANT: `count` must reflect REAL, current stock — never a fabricated
     or auto-decrementing number (false urgency is deceptive and can violate
     FTC guidance, consumer-protection law, and Shopify's terms). In the
     Shopify theme this is bound to the variant's real inventory_quantity;
     set to null to show a neutral "In Stock" badge instead of a count. */
  scarcity: {
    outdoorCount: null,
    indoorCount: null,
    lowThreshold: 20,       // only show a count when at/below this
    inStockLabel: "In Stock"
  },

  stickyCta: {
    label: "Add to Cart",
    /* Price is rendered from real variant data by main.js; no hard-coded
       "from $X" figure here, which would go stale the moment prices move. */
    priceNote: null,
    microcopy: "Free shipping on every order"
  },

  /* ============================================================
     INDOOR COLLECTION — "Indoor Comfort" tab
     StarOLED 2-in-1 Indoor UV Insect Trap & Quiet Ambient Night Light
     ============================================================ */
  indoor: {
    product: {
      name: "StarOLED 2-in-1 Indoor UV Insect Trap & Quiet Ambient Night Light",
      shortName: "StarOLED Indoor 2-in-1"
    },

    hero: {
      superHeadline: "QUIET, CORDLESS, CHEMICAL-FREE",
      headline: "A Quiet Indoor Insect Trap and Night Light in One.",
      subheadline:
        "A low-noise indoor UV insect trap and warm ambient night light in one. No fumes, no sprays, no chemicals—gentle enough for nurseries, quiet enough to leave running overnight.",
      /* No aggregate rating is displayed until Judge.me has collected real
         verified-purchase reviews. Never hard-code a star rating or review
         count here — see the testimonials block in copy-data.js. */
      ratingText: null,
      bullets: [
        {
          icon: "quiet",
          title: "Bedroom-Quiet (Under 20dB)",
          text: "A low-noise design made for sleeping spaces—soft UV attraction and a gentle night-light glow that won't disturb the lightest sleeper."
        },
        {
          icon: "battery",
          title: "USB-Rechargeable, Fully Portable",
          text: "Charge with the included USB cable, then move it anywhere—nightstand tonight, kitchen tomorrow, even the camping cabin. Includes a wall hook for hanging."
        },
        {
          icon: "child",
          title: "Zero Fumes, Sprays, or Chemicals",
          text: "UV light attracts and the caged electric grid does the rest — no chemicals released into the air your family breathes. Keep out of reach of small children, as with any electrical appliance."
        }
      ],
      cta: {
        urgency: "Free worldwide shipping on every order — dispatched in 1–3 business days."
      }
    },

    bundles: {
      heading: "Choose Your Indoor Coverage",
      currency: "$",
      options: [
        {
          id: "pack1",
          qty: 1,
          name: "Starter",
          price: 39.99,
          compareAt: null,
          shipping: 0,
          shippingLabel: "FREE Shipping",
          badge: null,
          savingsLabel: null,
          perUnit: 39.99
        },
        {
          id: "pack2",
          qty: 2,
          name: "Living Room + Bedroom Combo",
          price: 64.99,
          /* compareAt is the real cost of buying this many singly
             (39.99 x 2). It is a multi-buy saving, not a former retail
             price — never inflate it. */
          compareAt: 79.98,
          shipping: 0,
          shippingLabel: "FREE Shipping",
          badge: "Most Popular",
          savingsLabel: "Save 19%",
          perUnit: 32.5
        },
        {
          id: "pack4",
          qty: 4,
          name: "Whole House",
          price: 99.99,
          compareAt: 159.96,
          shipping: 0,
          shippingLabel: "FREE Shipping",
          badge: "Best Value",
          savingsLabel: "Save 37%",
          perUnit: 25.0
        }
      ]
    },

    /* Color variants — `image` is the gallery index that shows the color.
       Black model: images 1–6 · White model: image 7 */
    colors: [
      { id: "black", name: "Midnight Black", swatch: "#17181C", image: 1 },
      { id: "white", name: "Arctic White", swatch: "#F4F5F7", image: 7 }
    ],

    /* assets/indoor/1.webp … 8.webp */
    gallery: {
      count: 8,
      alt: {
        1: "StarOLED indoor UV insect trap lamp on a living room side table",
        2: "StarOLED indoor lamp features: soft night light, caged electric grid, chemical-free",
        3: "StarOLED 2-in-1 indoor UV insect trap and ambient light beside a bed",
        4: "StarOLED indoor lamp quiet operation and rechargeable battery overview",
        5: "StarOLED indoor lamp package contents, wall hook, USB cable, and dimensions",
        6: "StarOLED indoor lamp in night light mode next to a sleeping child",
        7: "StarOLED indoor lamp in white finish on a nightstand, rechargeable model",
        8: "StarOLED portable rechargeable lamp on a picnic table at a campsite tent"
      }
    },

    /* Indoor reviews — REAL REVIEWS ONLY. Intentionally empty; served by
       Judge.me from verified purchases. See the outdoor testimonials block
       above for the full rationale. Do not hand-write entries here. */
    testimonials: {
      heading: "Customer Reviews",
      subheading: "Every review below comes from a verified purchase. We do not write, edit, or filter them.",
      emptyState: {
        title: "No reviews yet",
        text: "The StarOLED Indoor lamp is newly launched, so there are no customer reviews to show yet. Buyers receive an email invitation to leave an honest review, and it appears here unedited."
      },
      reviews: []
    },

    faqs: {
      heading: "Everything You're Wondering",
      items: [
        {
          q: "How well does it work on mosquitoes indoors?",
          a: "Honestly, it is better at some insects than others. UV traps catch a broad range of flying insects, but mosquitoes are the hardest to catch this way, because they navigate by exhaled CO2 and body heat rather than UV light. Treat it as a night light that also reduces flying-insect traffic in the room — not as a replacement for screens, nets, or repellent."
        },
        {
          q: "How quiet is it, really?",
          a: "It runs at a low noise level designed for bedrooms and nurseries—there's no loud electric 'zap' crackle. It is quiet enough to leave running all night on a nightstand."
        },
        {
          q: "How is it powered — do I need it near an outlet?",
          a: "It's USB-rechargeable. Charge it with the included cable, then place it anywhere: a shelf, nightstand, or hung from the included wall hook. A full charge runs through the night, so there are no cords across the room."
        },
        {
          q: "Is it safe around children and pets?",
          a: "The internal grid is enclosed by a protective cage designed to keep fingers and paws away from it, and it uses no chemicals, fumes, or sprays — nothing to inhale or track around the house. As with any electrical appliance, keep it out of reach of small children, supervise young children around it, and do not insert objects through the cage."
        },
        {
          q: "Which rooms does it work best in?",
          a: "Bedrooms, living rooms, nurseries, kitchens, and offices. The UV light draws flying insects in from across the room, so one unit per main room gives the most even coverage in a larger home — which is what the 4-Pack is intended for."
        },
        {
          q: "How do I clean it?",
          a: "Switch it off, slide out the bottom tray, empty it, and wipe with the included brush. No chemicals or replacement cartridges are ever needed."
        }
      ]
    }
  }
};

/* Expose for main.js (plain script include, no build step) */
window.COPY = COPY;
window.STAROLED_COPY = COPY;
